import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getAdminStats,
  getSubscribers,
  getAdminBusinesses,
  getRevenue,
  toggleBusinessPublish,
  toggleBusinessFounding,
  adminDeleteBusiness,
  getAdminAnalytics,
  getChatAnalytics,
  getEscalations,
  resolveEscalation,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Users,
  Store,
  DollarSign,
  TrendingUp,
  Star,
  Eye,
  EyeOff,
  Trash2,
  Loader2,
  BarChart3,
  Award,
  MousePointerClick,
  Phone,
  Globe,
  Share2,
  MessageCircle,
  Mail,
  CheckCircle,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';

export default function Admin() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [chatAnalytics, setChatAnalytics] = useState(null);
  const [escalations, setEscalations] = useState([]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [isAdmin, navigate]);

  const fetchAllData = async () => {
    try {
      const [statsRes, subsRes, bizRes, revRes, analyticsRes, chatRes, escRes] = await Promise.all([
        getAdminStats(),
        getSubscribers(),
        getAdminBusinesses(),
        getRevenue(),
        getAdminAnalytics(),
        getChatAnalytics().catch(() => ({ data: null })),
        getEscalations().catch(() => ({ data: { escalations: [] } })),
      ]);
      setStats(statsRes.data);
      setSubscribers(subsRes.data.subscribers || []);
      setBusinesses(bizRes.data.businesses || []);
      setRevenue(revRes.data);
      setAnalytics(analyticsRes.data);
      setChatAnalytics(chatRes.data);
      setEscalations(escRes.data?.escalations || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFounding = async (businessId) => {
    try {
      const res = await toggleBusinessFounding(businessId);
      setBusinesses((prev) =>
        prev.map((b) =>
          b.id === businessId ? { ...b, is_founding: res.data.is_founding } : b
        )
      );
      toast.success(`Founding status ${res.data.is_founding ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update founding status');
    }
  };

  const handleTogglePublish = async (businessId) => {
    try {
      const res = await toggleBusinessPublish(businessId);
      setBusinesses((prev) =>
        prev.map((b) =>
          b.id === businessId ? { ...b, is_published: res.data.is_published } : b
        )
      );
      toast.success(`Business ${res.data.is_published ? 'published' : 'unpublished'}`);
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    try {
      await adminDeleteBusiness(businessId);
      setBusinesses((prev) => prev.filter((b) => b.id !== businessId));
      toast.success('Business deleted');
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const handleResolveEscalation = async (escalationId) => {
    try {
      await resolveEscalation(escalationId);
      setEscalations((prev) =>
        prev.map((e) =>
          e.id === escalationId ? { ...e, status: 'resolved' } : e
        )
      );
      toast.success('Escalation resolved');
    } catch (error) {
      toast.error('Failed to resolve escalation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 noise-bg" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage subscribers, listings, and view revenue analytics.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="stats-grid">
          {[
            {
              label: 'Total Users',
              value: stats?.total_users || 0,
              icon: Users,
              color: 'text-blue-500',
            },
            {
              label: 'Active Subscriptions',
              value: stats?.active_subscriptions || 0,
              icon: TrendingUp,
              color: 'text-green-500',
            },
            {
              label: 'Published Listings',
              value: stats?.published_businesses || 0,
              icon: Store,
              color: 'text-primary',
            },
            {
              label: 'Total Revenue',
              value: `$${revenue?.total_revenue?.toFixed(2) || '0.00'}`,
              icon: DollarSign,
              color: 'text-emerald-500',
            },
          ].map((stat, i) => (
            <div
              key={i}
              className="stat-card rounded-xl p-6"
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <stat.icon className={`w-8 h-8 ${stat.color} mb-3`} />
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-sm text-muted-foreground">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Revenue Breakdown */}
        <div className="glass-card rounded-2xl border border-border p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Revenue Breakdown</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-secondary/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">Basic Plans</div>
              <div className="text-xl font-semibold">
                {revenue?.plan_breakdown?.basic || 0} subscriptions
              </div>
            </div>
            <div className="bg-secondary/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">Featured Plans</div>
              <div className="text-xl font-semibold">
                {revenue?.plan_breakdown?.featured || 0} subscriptions
              </div>
            </div>
            <div className="bg-secondary/50 rounded-xl p-4">
              <div className="text-sm text-muted-foreground mb-1">Featured Listings</div>
              <div className="text-xl font-semibold flex items-center gap-2">
                <Star className="w-4 h-4 text-primary fill-current" />
                {stats?.featured_count || 0} active
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="subscribers" className="space-y-4">
          <TabsList className="bg-card border border-border">
            <TabsTrigger value="subscribers" data-testid="tab-subscribers">
              Subscribers
            </TabsTrigger>
            <TabsTrigger value="businesses" data-testid="tab-businesses">
              All Businesses
            </TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              Analytics
            </TabsTrigger>
            <TabsTrigger value="chat-support" data-testid="tab-chat-support">
              <MessageCircle className="w-4 h-4 mr-1.5" />
              Chat Support
              {chatAnalytics?.pending_escalations > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                  {chatAnalytics.pending_escalations}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Subscribers Tab */}
          <TabsContent value="subscribers" className="space-y-4">
            <div className="glass-card rounded-2xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscribers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No subscribers yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscribers.map((sub, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{sub.user?.name}</div>
                            <div className="text-sm text-muted-foreground">{sub.user?.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {sub.subscription?.plan_id}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={sub.subscription?.status === 'active' ? 'default' : 'secondary'}
                            className={
                              sub.subscription?.status === 'active'
                                ? 'bg-green-500/20 text-green-500'
                                : ''
                            }
                          >
                            {sub.subscription?.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{sub.business?.name || '—'}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {sub.subscription?.created_at
                            ? new Date(sub.subscription.created_at).toLocaleDateString()
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Businesses Tab */}
          <TabsContent value="businesses" className="space-y-4">
            <div className="glass-card rounded-2xl border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Business</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Founding</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businesses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No businesses registered
                      </TableCell>
                    </TableRow>
                  ) : (
                    businesses.map((biz) => (
                      <TableRow key={biz.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {biz.is_featured && (
                              <Star className="w-4 h-4 text-primary fill-current" />
                            )}
                            <span className="font-medium">{biz.name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{biz.category}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{biz.owner_name}</TableCell>
                        <TableCell>
                          <Badge
                            variant={biz.is_published ? 'default' : 'secondary'}
                            className={biz.is_published ? 'bg-green-500/20 text-green-500' : ''}
                          >
                            {biz.is_published ? 'Published' : 'Hidden'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={biz.is_founding ? 'default' : 'secondary'}
                            className={biz.is_founding ? 'bg-teal-500/20 text-teal-500' : ''}
                          >
                            {biz.is_founding ? 'Founding' : '—'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleFounding(biz.id)}
                              title="Toggle Founding Status"
                              data-testid={`founding-${biz.id}`}
                            >
                              <Award className={`w-4 h-4 ${biz.is_founding ? 'text-teal-500' : ''}`} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTogglePublish(biz.id)}
                              data-testid={`toggle-${biz.id}`}
                            >
                              {biz.is_published ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive hover:text-destructive"
                                  data-testid={`delete-${biz.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Business?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    This will permanently delete "{biz.name}". This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteBusiness(biz.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-4">
            <div className="glass-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">Platform Analytics</h2>
              </div>

              {/* Overview Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Total Views</span>
                  </div>
                  <div className="text-2xl font-bold">{analytics?.total_views || 0}</div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MousePointerClick className="w-4 h-4 text-accent" />
                    <span className="text-xs text-muted-foreground">Total Clicks</span>
                  </div>
                  <div className="text-2xl font-bold">{analytics?.total_clicks || 0}</div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Views (7 days)</span>
                  </div>
                  <div className="text-2xl font-bold">{analytics?.views_7d || 0}</div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Views (30 days)</span>
                  </div>
                  <div className="text-2xl font-bold">{analytics?.views_30d || 0}</div>
                </div>
              </div>

              {/* Click Breakdown */}
              <div className="border-t border-border pt-6 mb-8">
                <h3 className="text-sm font-semibold mb-4">Click Breakdown (All Time)</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <Phone className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <div className="text-lg font-bold">{analytics?.click_breakdown?.phone || 0}</div>
                    <div className="text-xs text-muted-foreground">Phone</div>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <Globe className="w-5 h-5 mx-auto mb-2 text-primary" />
                    <div className="text-lg font-bold">{analytics?.click_breakdown?.website || 0}</div>
                    <div className="text-xs text-muted-foreground">Website</div>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <Share2 className="w-5 h-5 mx-auto mb-2 text-blue-500" />
                    <div className="text-lg font-bold">{analytics?.click_breakdown?.facebook || 0}</div>
                    <div className="text-xs text-muted-foreground">Facebook</div>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <Share2 className="w-5 h-5 mx-auto mb-2 text-pink-500" />
                    <div className="text-lg font-bold">{analytics?.click_breakdown?.instagram || 0}</div>
                    <div className="text-xs text-muted-foreground">Instagram</div>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3 text-center">
                    <Share2 className="w-5 h-5 mx-auto mb-2 text-sky-500" />
                    <div className="text-lg font-bold">{analytics?.click_breakdown?.twitter || 0}</div>
                    <div className="text-xs text-muted-foreground">Twitter</div>
                  </div>
                </div>
              </div>

              {/* Top Businesses */}
              {analytics?.top_businesses?.length > 0 && (
                <div className="border-t border-border pt-6 mb-8">
                  <h3 className="text-sm font-semibold mb-4">Top Businesses by Views</h3>
                  <div className="space-y-3">
                    {analytics.top_businesses.map((biz, i) => (
                      <div key={i} className="flex items-center justify-between bg-secondary/30 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary">
                            {i + 1}
                          </div>
                          <div>
                            <div className="font-medium">{biz.name}</div>
                            <div className="text-xs text-muted-foreground">{biz.subcategory || biz.category}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4 text-muted-foreground" />
                          <span className="font-semibold">{biz.views}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Categories */}
              {analytics?.top_categories?.length > 0 && (
                <div className="border-t border-border pt-6">
                  <h3 className="text-sm font-semibold mb-4">Most Viewed Categories</h3>
                  <div className="flex flex-wrap gap-3">
                    {analytics.top_categories.map((cat, i) => (
                      <Badge key={i} variant="secondary" className="text-sm py-2 px-4">
                        {cat.category}: {cat.views} views
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {(analytics?.total_views === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No analytics data yet. Views and clicks will appear here as users browse the directory.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Chat Support Tab */}
          <TabsContent value="chat-support" className="space-y-4">
            <div className="glass-card rounded-2xl border border-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <MessageCircle className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold">Chat Support Analytics</h2>
              </div>

              {/* Chat Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageCircle className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground">Total Conversations</span>
                  </div>
                  <div className="text-2xl font-bold">{chatAnalytics?.total_conversations || 0}</div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <HelpCircle className="w-4 h-4 text-blue-500" />
                    <span className="text-xs text-muted-foreground">Total Messages</span>
                  </div>
                  <div className="text-2xl font-bold">{chatAnalytics?.total_messages || 0}</div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-amber-500" />
                    <span className="text-xs text-muted-foreground">Total Escalations</span>
                  </div>
                  <div className="text-2xl font-bold">{chatAnalytics?.total_escalations || 0}</div>
                </div>
                <div className="bg-secondary/50 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-xs text-muted-foreground">Escalation Rate</span>
                  </div>
                  <div className="text-2xl font-bold">{chatAnalytics?.escalation_rate || 0}%</div>
                </div>
              </div>

              {/* Time-based Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold">{chatAnalytics?.messages_7d || 0}</div>
                  <div className="text-xs text-muted-foreground">Messages (7 days)</div>
                </div>
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold">{chatAnalytics?.messages_30d || 0}</div>
                  <div className="text-xs text-muted-foreground">Messages (30 days)</div>
                </div>
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold">{chatAnalytics?.escalations_7d || 0}</div>
                  <div className="text-xs text-muted-foreground">Escalations (7 days)</div>
                </div>
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold">{chatAnalytics?.escalations_30d || 0}</div>
                  <div className="text-xs text-muted-foreground">Escalations (30 days)</div>
                </div>
              </div>

              {/* Top Questions */}
              {chatAnalytics?.top_questions?.length > 0 && (
                <div className="border-t border-border pt-6 mb-8">
                  <h3 className="text-sm font-semibold mb-4">Most Popular Questions</h3>
                  <div className="space-y-2">
                    {chatAnalytics.top_questions.map((q, i) => (
                      <div key={i} className="flex items-center justify-between bg-secondary/30 rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                            {i + 1}
                          </div>
                          <span className="text-sm truncate max-w-[400px]">{q.question}</span>
                        </div>
                        <Badge variant="secondary">{q.count} times</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!chatAnalytics || chatAnalytics?.total_messages === 0) && (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No chat data yet. Conversations will appear here as users interact with the support chat.</p>
                </div>
              )}
            </div>

            {/* Pending Escalations */}
            <div className="glass-card rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-semibold">Support Escalations</h2>
                </div>
                {chatAnalytics?.pending_escalations > 0 && (
                  <Badge variant="destructive">{chatAnalytics.pending_escalations} pending</Badge>
                )}
              </div>

              {escalations.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No escalation requests yet. Users who need human support will appear here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {escalations.map((esc) => (
                    <div key={esc.id} className={`border rounded-xl p-4 ${esc.status === 'pending' ? 'border-amber-500/50 bg-amber-500/5' : 'border-border bg-secondary/30'}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-medium">{esc.email}</span>
                            <Badge variant={esc.status === 'pending' ? 'destructive' : 'secondary'} className="text-xs">
                              {esc.status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">"{esc.message}"</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(esc.created_at).toLocaleString()}
                          </p>
                        </div>
                        {esc.status === 'pending' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleResolveEscalation(esc.id)}
                            className="ml-4"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
